import React, { useState } from "react";
import { X, Globe, PhoneCall, CheckCircle2 } from "lucide-react";
import { useT } from "../context/LanguageContext";
import { LeadForm } from "./LeadCaptureModal";
import VoiceAgentModal from "./VoiceAgentModal";
import { submitAgentLead, triggerAgentCall } from "../services/marketingApi";

/*
 * Replaces the old "click → immediately connect" flow for the
 * "Ajanla Konuş" entry points (Hero, FAQSection). Steps:
 *   1. choice   — web call vs. phone call
 *   2. lead     — collect name/email/phone (persisted to the backend)
 *   3a. web     — hands off to VoiceAgentModal, which auto-connects
 *   3b. calling — confirms a real outbound call was triggered
 */
const AgentAccessModal = ({ open, onClose }) => {
  const t = useT();
  const [step, setStep] = useState("choice"); // choice | lead | web | calling
  const [channel, setChannel] = useState(null); // "web" | "phone"
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [calledPhone, setCalledPhone] = useState("");

  const reset = () => {
    setStep("choice");
    setChannel(null);
    setSubmitting(false);
    setError("");
    setCalledPhone("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const chooseChannel = (ch) => {
    setChannel(ch);
    setError("");
    setStep("lead");
  };

  const handleLeadSubmit = async (values) => {
    setSubmitting(true);
    setError("");
    try {
      if (channel === "web") {
        await submitAgentLead({ ...values, source: "voice_web" });
        setStep("web");
      } else {
        await triggerAgentCall(values);
        setCalledPhone(values.phone);
        setStep("calling");
      }
    } catch (err) {
      setError(err?.message || t("agentAccess.calling.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // Once the web channel's lead is captured, hand off entirely to
  // VoiceAgentModal (it auto-connects as soon as it's opened).
  if (step === "web") {
    return <VoiceAgentModal open={true} onClose={handleClose} />;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(15,21,48,0.55)", backdropFilter: "blur(8px)" }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:opacity-70 z-10"
          style={{ color: "var(--fg)" }}
          aria-label="Kapat"
        >
          <X size={18} />
        </button>

        {step === "choice" && (
          <div className="px-7 pt-8 pb-7">
            <h3 className="text-[20px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
              {t("agentAccess.choice.title")}
            </h3>
            <p className="mt-1.5 text-[13.5px]" style={{ color: "var(--muted)" }}>
              {t("agentAccess.choice.subtitle")}
            </p>

            <div className="mt-6 space-y-3">
              <button
                onClick={() => chooseChannel("web")}
                className="w-full flex items-center gap-4 p-4 rounded-2xl neon-box text-left transition-transform hover:scale-[1.01]"
                style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-cyan)15", color: "var(--brand-cyan)" }}
                >
                  <Globe size={20} />
                </div>
                <div>
                  <p className="text-[14.5px] font-semibold" style={{ color: "var(--fg)" }}>
                    {t("agentAccess.choice.web.title")}
                  </p>
                  <p className="text-[12.5px]" style={{ color: "var(--muted)" }}>
                    {t("agentAccess.choice.web.desc")}
                  </p>
                </div>
              </button>

              <button
                onClick={() => chooseChannel("phone")}
                className="w-full flex items-center gap-4 p-4 rounded-2xl neon-box text-left transition-transform hover:scale-[1.01]"
                style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-coral)15", color: "var(--brand-coral)" }}
                >
                  <PhoneCall size={20} />
                </div>
                <div>
                  <p className="text-[14.5px] font-semibold" style={{ color: "var(--fg)" }}>
                    {t("agentAccess.choice.phone.title")}
                  </p>
                  <p className="text-[12.5px]" style={{ color: "var(--muted)" }}>
                    {t("agentAccess.choice.phone.desc")}
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}

        {step === "lead" && (
          <div className="px-7 pt-7 pb-7">
            <h3 className="text-[20px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
              {t("agentAccess.lead.title")}
            </h3>
            <p className="mt-1.5 mb-5 text-[13.5px]" style={{ color: "var(--muted)" }}>
              {t("agentAccess.lead.subtitle")}
            </p>
            <LeadForm
              onSubmit={handleLeadSubmit}
              submitting={submitting}
              error={error}
              onBack={() => setStep("choice")}
            />
          </div>
        )}

        {step === "calling" && (
          <div className="px-7 py-10 flex flex-col items-center text-center">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ background: "var(--brand-cyan)15" }}
            >
              <CheckCircle2 size={28} style={{ color: "var(--brand-cyan)" }} />
            </div>
            <h4 className="text-[18px] font-semibold mb-2" style={{ color: "var(--fg)" }}>
              {t("agentAccess.calling.title")}
            </h4>
            <p className="text-[14px] max-w-sm" style={{ color: "var(--muted)" }}>
              {t("agentAccess.calling.message").replace("{phone}", calledPhone)}
            </p>
            <button
              onClick={handleClose}
              className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[14px] font-semibold neon-btn"
            >
              {t("agentAccess.calling.close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAccessModal;
